<template>
  <div class="flex items-center gap-2">
    <div
      v-if="!isLoggedIn"
      class="w-40 px-2 text-xs text-gray-500 xl:w-48 xl:p-0 xl:text-sm"
      :class="nav ? 'text-left' : 'hidden xl:block xl:text-right'"
    >
      <RouterLink to="/login" class="rounded border p-2 xl:border-0 xl:p-0"> 로그인 </RouterLink>
    </div>
    <div v-else ref="userMenuRef" class="relative" :class="nav ? 'text-left' : 'hidden xl:flex'">
      <button
        type="button"
        class="flex w-40 items-center justify-between gap-1 rounded-md bg-gray-100 p-1.5 xl:w-48 xl:p-2"
        :aria-expanded="open ? 'true' : 'false'"
        :aria-controls="menuId"
        @click="toggleNav"
      >
        <div class="flex min-w-0 flex-1 items-center gap-1 xl:gap-2">
          <div
            class="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary-light"
          >
            <CircleUserRound class="w-4 text-white" />
          </div>
          <span class="truncate text-xs xl:text-sm">{{ userName }}</span>
        </div>
        <ChevronRight class="w-3.5 shrink-0 text-gray-500" />
      </button>

      <transition
        enter-active-class="transition ease-out duration-150"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition ease-in duration-100"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <ul
          v-if="open"
          :id="menuId"
          role="menu"
          class="absolute right-0 top-full z-50 mt-2 min-w-32 rounded-md border bg-white text-xs shadow"
          tabindex="-1"
        >
          <li class="hover:bg-gray-50">
            <RouterLink to="/mypage" class="flex items-center gap-1.5 p-2 px-4 text-left">
              <UserRound class="w-4" />
              마이페이지
            </RouterLink>
          </li>
          <li class="hover:bg-gray-50">
            <button
              class="flex w-full items-center gap-1.5 p-2 px-4 text-left"
              @click="handleLogout"
            >
              <DoorOpen class="w-4" />
              로그아웃
            </button>
          </li>
        </ul>
      </transition>
    </div>

    <button
      v-if="!props.nav"
      type="button"
      class="inline-flex items-center p-2 xl:hidden"
      aria-label="메뉴 열기"
      @click="$emit('toggle-nav')"
    >
      <Menu class="h-5 w-5 md:h-6 md:w-6" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";

import { ChevronRight, CircleUserRound, DoorOpen, Menu, UserRound } from "lucide-vue-next";
import { logoutApi } from "~/composable/AuthApi";
import { authorizationApi } from "~/composable/BaseApi";
import { useAuthStore } from "~/stores/authStore";

const { $_ } = useNuxtApp();
const router = useRouter();
const authStore = useAuthStore();
const { me, isInitializeCompleted } = storeToRefs(authStore);

const props = defineProps<{
  nav?: boolean;
  open?: boolean;
}>();

const emit = defineEmits<{
  (e: "toggle-nav"): void;
}>();

const isLoggedIn = computed(() => !$_.isEmpty(me.value));

const userName = computed(() => $_.get(me, ["value", "flnm"]));

const open = ref(false);
const userMenuRef = ref<HTMLElement | null>(null);
const menuId = `user-menu-${Math.random().toString(36).slice(2, 8)}`;

const toggleNav = () => {
  open.value = !open.value;
};

const closeNav = () => {
  open.value = false;
};

const onPointerDown = (e: PointerEvent) => {
  const root = userMenuRef.value;
  if (!root) return;
  if (root.contains(e.target as Node)) return;
  closeNav();
};

const onKeyDown = (e: KeyboardEvent) => {
  if (e.key === "Escape") {
    closeNav();
  }
};

onMounted(() => {
  document.addEventListener("pointerdown", onPointerDown, true);
  document.addEventListener("keydown", onKeyDown);
});

onBeforeUnmount(() => {
  document.removeEventListener("pointerdown", onPointerDown, true);
  document.removeEventListener("keydown", onKeyDown);
});

const handleLogout = async () => {
  closeNav();
  await logoutApi();
  await authorizationApi();
  router.push({ path: "/" });
};
</script>
